import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { useAuth } from '../contexts/AuthContext';

export const MAX_RECENT_DEAL_VIEWS = 10;

/**
 * Pure helper: prepend a view of `dealId` to the list, deduping any prior
 * entry for the same deal and capping the list length. Newest-first.
 *
 * Entries are `{ dealId, viewedAt }`. Only IDs are stored — deal details are
 * hydrated fresh from Supabase at display time, which is what enforces owner
 * scoping and self-heals deleted/reassigned deals.
 */
export function buildNextRecentViews(current, dealId, max = MAX_RECENT_DEAL_VIEWS) {
  const list = Array.isArray(current) ? current.filter((entry) => entry && entry.dealId) : [];
  if (!dealId) return list.slice(0, max);
  const withoutDeal = list.filter((entry) => entry.dealId !== dealId);
  return [{ dealId, viewedAt: new Date().toISOString() }, ...withoutDeal].slice(0, max);
}

/**
 * Per-user "recently viewed deals" history, persisted on the Firestore user
 * doc (users/{uid}.recentDealViews) — same home as seenTours and
 * crmTablePreferences, so it follows the user across devices.
 *
 * Writes are optimistic: local state updates immediately; the Firestore write
 * failing is non-fatal (history just won't sync).
 */
export function useRecentDealViews() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;

  const [recentViews, setRecentViews] = useState([]);
  const [loading, setLoading] = useState(true);
  // Ref mirrors state so recordDealView stays referentially stable, and
  // loadPromiseRef lets recordDealView wait for the initial load — writing
  // before the load resolves would clobber history from other sessions.
  const viewsRef = useRef([]);
  const loadPromiseRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      viewsRef.current = [];
      loadPromiseRef.current = null;
      setRecentViews([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    loadPromiseRef.current = (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        const stored = (snap.exists() && snap.data().recentDealViews) || [];
        const list = Array.isArray(stored) ? stored.filter((entry) => entry && entry.dealId) : [];
        if (!cancelled) {
          viewsRef.current = list;
          setRecentViews(list);
        }
      } catch (error) {
        console.error('RecentDealViews: failed to load', error);
        if (!cancelled) {
          viewsRef.current = [];
          setRecentViews([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const recordDealView = useCallback(
    async (dealId) => {
      if (!dealId || !uid) return;
      if (loadPromiseRef.current) await loadPromiseRef.current;
      // Already the most recent view — skip the redundant write (realtime
      // deal updates and re-renders re-fire the caller's effect).
      if (viewsRef.current[0]?.dealId === dealId) return;
      const next = buildNextRecentViews(viewsRef.current, dealId);
      viewsRef.current = next;
      setRecentViews(next);
      try {
        await updateDoc(doc(db, 'users', uid), { recentDealViews: next });
      } catch (error) {
        console.error('RecentDealViews: failed to persist', error);
      }
    },
    [uid]
  );

  return { recentViews, loading, recordDealView };
}
